/** Assembled schema-driven config grid — reusable across consumers.
 *
 * Extracted (behaviour-preserving) from the grid body of
 * `../AssistedConfigEditor.tsx`. Owns purely-presentational state (search,
 * expansion, selection, add-key, pending-delete) and emits every mutation
 * through `onChange(nextValue)` with an immutably-updated copy. It deliberately
 * does NOT own save, optimistic concurrency, or any API calls — those belong to
 * the consumer.
 */
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import { AddPropertyPicker } from '../AddPropertyPicker';
import { ConfigRowItem, type RenderValueFn } from './ConfigRowItem';
import { HelpPane } from './HelpPane';
import { buildRows, getDefaultValue, removeNestedKey, removePaths, setNestedValue } from './schemaOps';
import type { ConfigRow, SchemaObject } from './types';

export interface SchemaConfigGridProps {
  schema: SchemaObject | null | undefined;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Top-level key prefixes hidden from DISPLAY only (never removed from
   *  `value`). Forwarded to `buildRows`. */
  hiddenKeyPrefixes?: string[];
  readOnly?: boolean;
  /** Optional custom-widget renderer, dispatched per row (e.g. by x-widget). */
  renderValue?: RenderValueFn;
  /** Advisory config-schema warnings keyed by dot-joined field path → message.
   *  Highlights the matching rows and auto-expands their parent groups. */
  warningsByPath?: Record<string, string>;
}

export function SchemaConfigGrid({
  schema,
  value,
  onChange,
  hiddenKeyPrefixes,
  readOnly = false,
  renderValue,
  warningsByPath,
}: SchemaConfigGridProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [addKey, setAddKey] = useState<string | null>(null);
  // Unknown (not-in-schema) keys are collapsed by default — they're rare and noisy.
  const [unknownExpanded, setUnknownExpanded] = useState(false);
  // Checked row ids (set of row.id strings) for batch operations.
  const [checked, setChecked] = useState<Set<string>>(new Set());
  // Pending deletion confirmation — `null` means no dialog is open.
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: 'single'; rowId: string; path: string[] }
    | { kind: 'batch'; paths: string[][]; rowIds: string[] }
    | null
  >(null);

  const schemaProps = schema?.properties;
  const canEdit = !readOnly;

  const allRows = useMemo(
    () =>
      schemaProps
        ? buildRows(schemaProps, value, { search, expanded, hiddenKeyPrefixes })
        : [],
    [schemaProps, value, search, expanded, hiddenKeyPrefixes],
  );

  // Split the flat row list back into schema rows and the trailing "unknown"
  // top-level section (rows whose id was namespaced by buildRows).
  const { schemaRows, unknownRows } = useMemo(() => {
    const s: ConfigRow[] = [];
    const u: ConfigRow[] = [];
    for (const row of allRows) {
      if (row.id.startsWith('__unknown.')) u.push(row);
      else s.push(row);
    }
    return { schemaRows: s, unknownRows: u };
  }, [allRows]);

  const selectedRow = allRows.find((r) => r.id === selected);

  const availableToAdd = useMemo(
    () =>
      schemaProps
        ? Object.entries(schemaProps)
            .filter(([k]) => !(k in value))
            .map(([k, v]) => ({ key: k, prop: v }))
            .sort((a, b) => a.key.localeCompare(b.key))
        : [],
    [schemaProps, value],
  );

  // Reveal nested warnings: expand the parent group of any warned child path.
  useEffect(() => {
    if (!warningsByPath) return;
    const parents = Object.keys(warningsByPath)
      .filter((p) => p.includes('.'))
      .map((p) => p.split('.')[0])
      .filter((k): k is string => !!k);
    if (parents.length) setExpanded((prev) => new Set([...prev, ...parents]));
  }, [warningsByPath]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }, []);

  function handleChange(path: string[], next: unknown) {
    onChange(setNestedValue(value, path.join('.'), next));
  }

  function handleAdd() {
    if (!addKey || !schemaProps) return;
    const prop = schemaProps[addKey];
    if (!prop) return;
    const defaultValue = getDefaultValue(prop);
    onChange({ ...value, [addKey]: defaultValue });
    if (prop.type === 'object') {
      setExpanded((prev) => new Set([...prev, addKey]));
    }
    setSelected(addKey);
    setAddKey(null);
  }

  // ---- Selection (checkboxes) ----
  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }, []);
  const clearChecked = useCallback(() => setChecked(new Set()), []);

  // ---- Deletion ----
  function requestSingleDelete(rowId: string, path: string[]) {
    setPendingDelete({ kind: 'single', rowId, path });
  }

  function requestBatchDelete() {
    if (checked.size === 0) return;
    const toDelete = allRows.filter((r) => checked.has(r.id));
    if (toDelete.length === 0) return;
    setPendingDelete({
      kind: 'batch',
      paths: toDelete.map((r) => r.path),
      rowIds: toDelete.map((r) => r.id),
    });
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    if (pendingDelete.kind === 'single') {
      onChange(removeNestedKey(value, pendingDelete.path.join('.')));
      setChecked((prev) => {
        const s = new Set(prev);
        s.delete(pendingDelete.rowId);
        return s;
      });
      if (selected === pendingDelete.rowId) setSelected(null);
    } else {
      onChange(
        removePaths(
          value,
          pendingDelete.paths.map((p) => p.join('.')),
        ),
      );
      setChecked((prev) => {
        const s = new Set(prev);
        pendingDelete.rowIds.forEach((id) => s.delete(id));
        return s;
      });
      if (selected && pendingDelete.rowIds.includes(selected)) setSelected(null);
    }
    setPendingDelete(null);
  }

  // When there is no schema, render a small placeholder rather than a grid.
  if (!schemaProps) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
      {/* Left: list */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search key or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {canEdit && (
            <>
              <AddPropertyPicker
                options={availableToAdd.map((o) => ({ key: o.key, type: o.prop.type }))}
                value={addKey}
                onChange={setAddKey}
                renderHelp={(key) => <HelpPane prop={schemaProps?.[key] ?? null} path={key} />}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!addKey}
                onClick={handleAdd}
                size="small"
              >
                Add
              </Button>
            </>
          )}
        </Box>

        {/* Row count */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, flexShrink: 0 }}>
          {schemaRows.length} schema key{schemaRows.length !== 1 ? 's' : ''}
          {unknownRows.length > 0 && ` · ${unknownRows.length} unknown`}
        </Typography>

        {/* Batch action toolbar — visible when ≥1 row is selected */}
        {checked.size > 0 && canEdit && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              p: 1,
              bgcolor: 'action.selected',
              borderRadius: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="body2">
              <strong>{checked.size}</strong> key{checked.size !== 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" onClick={clearChecked}>
              Clear
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              startIcon={<DeleteOutlineIcon />}
              onClick={requestBatchDelete}
            >
              Delete selected
            </Button>
          </Box>
        )}

        {/* Scrollable list */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {schemaRows.length === 0 && !unknownRows.length && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              {search ? 'No keys match your search.' : 'No configuration keys.'}
            </Typography>
          )}
          {schemaRows.map((row) => (
            <ConfigRowItem
              key={row.id}
              row={row}
              selected={selected === row.id}
              readOnly={!canEdit}
              deletable={canEdit}
              checked={checked.has(row.id)}
              onSelect={() => setSelected(row.id)}
              onToggleExpand={() => toggleExpand(row.id)}
              onChange={(v) => handleChange(row.path, v)}
              onRequestDelete={() => requestSingleDelete(row.id, row.path)}
              onToggleCheck={() => toggleCheck(row.id)}
              renderValue={renderValue}
              warning={warningsByPath?.[row.path.join('.')]}
            />
          ))}

          {/* Unknown keys section — collapsed by default */}
          {unknownRows.length > 0 && (
            <>
              <Box
                onClick={() => setUnknownExpanded((v) => !v)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1, mt: 1, cursor: 'pointer' }}
              >
                <Divider sx={{ flex: 1 }} />
                {unknownExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {unknownExpanded ? 'Hide' : 'Show'} {unknownRows.length} unknown key
                  {unknownRows.length !== 1 ? 's' : ''} (not in schema)
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
              {unknownExpanded &&
                unknownRows.map((row) => (
                <ConfigRowItem
                  key={row.id}
                  row={row}
                  selected={selected === row.id}
                  readOnly
                  deletable={canEdit}
                  checked={checked.has(row.id)}
                  onSelect={() => setSelected(row.id)}
                  onToggleExpand={() => {}}
                  onChange={() => {}}
                  onRequestDelete={() => requestSingleDelete(row.id, row.path)}
                  onToggleCheck={() => toggleCheck(row.id)}
                  warning={warningsByPath?.[row.path.join('.')]}
                />
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Right: help pane */}
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          borderLeft: 1,
          borderColor: 'divider',
          overflowY: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        {selectedRow ? (
          <HelpPane prop={selectedRow.schema} path={selectedRow.path.join('.')} />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select a row to see its description, type, and default value.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete confirmation dialog (single or batch) */}
      {pendingDelete && (
        <ConfirmDialog
          open
          destructive
          title={
            pendingDelete.kind === 'single'
              ? 'Remove configuration key?'
              : `Remove ${pendingDelete.paths.length} configuration keys?`
          }
          message={
            pendingDelete.kind === 'single'
              ? `This will remove "${pendingDelete.path.join('.')}" from the configuration.`
              : `This will remove these keys from the configuration:\n\n${pendingDelete.paths
                  .map((p) => '• ' + p.join('.'))
                  .join('\n')}`
          }
          confirmLabel="Remove"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </Box>
  );
}
